import { useThemeContext } from "@cohesive/ui-components";
import DOMPurify from "dompurify";
import React, { forwardRef, useEffect, useRef } from "react";

interface EmailPreviewIframeProps {
  content: string;
  onChange: (content: string) => void;
  readonly?: boolean;
}

export const EmailPreviewIframe = forwardRef<
  HTMLIFrameElement,
  EmailPreviewIframeProps
>(({ content, onChange, readonly = false }, forwardedRef) => {
  const internalRef = useRef<HTMLIFrameElement>(null);
  const iframeCleanupRef = useRef<(() => void) | null>(null);
  const isUpdatingFromPropsRef = useRef(false);
  const { mode } = useThemeContext();

  // Callback ref to sync both internal and forwarded refs
  const setRefs = React.useCallback(
    (node: HTMLIFrameElement | null) => {
      // Set internal ref
      internalRef.current = node;

      // Set forwarded ref
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        (
          forwardedRef as React.MutableRefObject<HTMLIFrameElement | null>
        ).current = node;
      }
    },
    [forwardedRef],
  );

  // Get theme colors
  const isDark = mode === "dark";
  const backgroundColor = isDark ? "#171717" : "#ffffff"; // neutral-900 : white
  const textColor = isDark ? "#fafafa" : "#0a0a0a"; // neutral-50 : neutral-950
  const linkColor = isDark ? "#60a5fa" : "#2563eb"; // blue-400 : blue-600

  useEffect(() => {
    const iframeEl = internalRef.current;
    if (!iframeEl) return;

    const doc = iframeEl.contentDocument || iframeEl.contentWindow?.document;
    if (!doc) return;

    // Initialize iframe with content
    const initializeIframe = () => {
      // Cleanup previous listeners
      if (iframeCleanupRef.current) {
        try {
          iframeCleanupRef.current();
        } catch (e) {
          console.error("Error cleaning up iframe listeners:", e);
        }
        iframeCleanupRef.current = null;
      }

      // Sanitize and set content
      // Remove color-related inline styles to allow theme colors to apply
      const sanitized = DOMPurify.sanitize(content || "", {
        ADD_TAGS: ["style"],
        ADD_ATTR: ["style"],
        FORBID_ATTR: [], // Don't forbid attributes, but we'll clean them after
      });

      // Further clean the HTML to remove color attributes that conflict with theme
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sanitized;

      // Remove inline color styles to allow theme colors
      const elementsWithStyle = tempDiv.querySelectorAll("[style]");
      elementsWithStyle.forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.style.color) {
          htmlEl.style.color = "";
        }
        // Clean up empty style attributes
        if (htmlEl.getAttribute("style")?.trim() === "") {
          htmlEl.removeAttribute("style");
        }
      });

      const cleanedContent = tempDiv.innerHTML;

      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                font-size: 14px;
                line-height: 1.5;
                margin: 12px;
                background-color: ${backgroundColor} !important;
                color: ${textColor} !important;
              }
              * {
                box-sizing: border-box;
              }
              /* Force theme colors on all elements */
              body * {
                color: ${textColor} !important;
              }
              a {
                color: ${linkColor} !important;
              }
              /* Preserve background colors that were explicitly set */
              [style*="background-color"] {
                background-color: inherit;
              }
            </style>
          </head>
          <body>${cleanedContent}</body>
        </html>
      `);
      doc.close();

      // Set design mode if not readonly
      if (!readonly) {
        doc.designMode = "on";

        // Throttle updates to avoid excessive React state updates
        let throttleTimer: number | undefined;
        const scheduleUpdate = () => {
          if (throttleTimer !== undefined) return;
          throttleTimer = window.setTimeout(() => {
            throttleTimer = undefined;
            try {
              if (!isUpdatingFromPropsRef.current) {
                const updated = doc.body?.innerHTML || "";
                onChange(updated);
              }
            } catch (e) {
              console.error("Failed to read iframe contents:", e);
            }
          }, 150);
        };

        const updateFromIframe = () => scheduleUpdate();

        // Listen for changes
        doc.addEventListener("input", updateFromIframe);
        doc.addEventListener("paste", updateFromIframe);
        doc.addEventListener("cut", updateFromIframe);

        // Store cleanup function
        iframeCleanupRef.current = () => {
          if (throttleTimer !== undefined) {
            clearTimeout(throttleTimer);
          }
          doc.removeEventListener("input", updateFromIframe);
          doc.removeEventListener("paste", updateFromIframe);
          doc.removeEventListener("cut", updateFromIframe);
        };
      } else {
        doc.designMode = "off";
      }
    };

    initializeIframe();

    return () => {
      if (iframeCleanupRef.current) {
        try {
          iframeCleanupRef.current();
        } catch (e) {
          console.error("Error during cleanup:", e);
        }
      }
    };
  }, [readonly, backgroundColor, textColor, linkColor]);

  // Update content when it changes externally
  useEffect(() => {
    const iframeEl = internalRef.current;
    if (!iframeEl) return;

    const doc = iframeEl.contentDocument || iframeEl.contentWindow?.document;
    if (!doc || !doc.body) return;

    const currentContent = doc.body.innerHTML;

    // Sanitize and clean content
    const sanitized = DOMPurify.sanitize(content || "", {
      ADD_TAGS: ["style"],
      ADD_ATTR: ["style"],
      FORBID_ATTR: [],
    });

    // Remove inline color styles
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitized;
    const elementsWithStyle = tempDiv.querySelectorAll("[style]");
    elementsWithStyle.forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.style.color) {
        htmlEl.style.color = "";
      }
      if (htmlEl.getAttribute("style")?.trim() === "") {
        htmlEl.removeAttribute("style");
      }
    });
    const cleanedContent = tempDiv.innerHTML;

    // Only update if content is different to avoid cursor issues
    if (currentContent !== cleanedContent) {
      isUpdatingFromPropsRef.current = true;
      doc.body.innerHTML = cleanedContent;
      setTimeout(() => {
        isUpdatingFromPropsRef.current = false;
      }, 200);
    }
  }, [content]);

  return (
    <iframe
      ref={setRefs}
      className="w-full min-h-[300px] border-0"
      style={{ backgroundColor }}
      title="Email Content Preview"
      sandbox="allow-same-origin"
    />
  );
});

EmailPreviewIframe.displayName = "EmailPreviewIframe";
